/**
 * How the note model is set to a score: the one shape every way of asking writes.
 *
 * The note model is never fitted again; what it follows of the score is decided by what is put
 * on it, and that is four things: which add-ons (`hints`), which layers the `layers` add-on is
 * told (`layers`), how far the fitted ones are put on (`strength`), and how much more than the
 * score the `more` add-on is asked for (`more`). The board's switches, the HTTP request
 * (`ArrangeRequest`), `rk-arrange` (`--hint`, `--layer`, `--strength`, `--more`) and a request in
 * words (`AskSettings`) all write this and nothing else, so whatever decides -- a reader, a
 * script, or a model reading words -- decides the same settings, each of which can be put on,
 * left off or turned down by itself.
 */

import {
  ARRANGE_HELD,
  ARRANGE_HINTS,
  ARRANGE_LAYERS,
  MORE_USUAL,
  type ArrangeHint,
  type ArrangeLayer,
  type ArrangeMore,
} from "./ai";

export interface ArrangeFollow {
  /** The add-ons put on, fitted and held; with none fitted the model is shown the tune alone. */
  hints: readonly ArrangeHint[];
  /** The layers the `layers` add-on is told; read only where `hints` has it. */
  layers: readonly ArrangeLayer[];
  /** How far the fitted add-ons are put on, from 0 to 1. */
  strength: number;
  /** How much more than the score the `more` add-on is asked for; read only where `hints` has it. */
  more: ArrangeMore;
}

/** The add-ons of `hints` that were fitted: those the strength is of. */
export function fittedOf(hints: readonly ArrangeHint[]): ArrangeHint[] {
  return hints.filter((hint) => !ARRANGE_HELD.includes(hint));
}

/** The fields of `follow` as an `ArrangeRequest` takes them. */
export function followSent(follow: ArrangeFollow): {
  hints: readonly ArrangeHint[];
  strength: number;
  layers?: readonly ArrangeLayer[];
  more?: ArrangeMore;
} {
  return {
    hints: follow.hints,
    strength: fittedOf(follow.hints).length === 0 ? 1 : follow.strength,
    ...(follow.hints.includes("layers") ? { layers: follow.layers } : {}),
    ...(follow.hints.includes("more") ? { more: follow.more } : {}),
  };
}

/**
 * Four ways of following a score, each a setting of the switches and nothing more, so a preset
 * is only a place to start from: every switch it sets can then be moved by itself. `keep` puts
 * the `together` add-on on, the setting that stays closest to the score's own accompaniment of
 * all those counted. `add` puts the `more` add-on on at its usual amount: the score is told in
 * full and each player asked for more strokes than its share of it has, which on the held-out
 * cuts is 1.7 times the moments the score strikes at with the harmony kept as `keep` keeps it.
 * The other two are settings of the `layers` add-on, the one that can leave a layer out:
 * `loosen` tells it the score's harmony and rhythm and where a bar comes again but not how
 * much is each player's, so the players share the bar out themselves; `change` tells it the
 * harmony alone, at full strength, so the rhythm and the figures are the model's own. Neither
 * of those two adds to the score, which is why adding is an add-on of its own (docs/07, "The
 * note model's add-ons"). A preset is a way of following the score, and a held add-on follows
 * nothing of it: one that is on stays on when a preset is taken (`presetOn`), and is not what
 * tells one preset from another (`followPreset`).
 */
export const ARRANGE_PRESETS = {
  keep: { hints: ["together"], layers: ARRANGE_LAYERS, strength: 1, more: MORE_USUAL },
  add: { hints: ["more"], layers: ARRANGE_LAYERS, strength: 1, more: MORE_USUAL },
  loosen: {
    hints: ["layers"],
    layers: ["harmony", "rhythm", "again"],
    strength: 1,
    more: MORE_USUAL,
  },
  change: { hints: ["layers"], layers: ["harmony"], strength: 1, more: MORE_USUAL },
} as const satisfies Record<string, ArrangeFollow>;
export type ArrangePreset = keyof typeof ARRANGE_PRESETS;
export const ARRANGE_PRESET_NAMES = Object.keys(ARRANGE_PRESETS) as ArrangePreset[];

/** `follow` set as the preset `name` sets it, the held add-ons left as they stand. */
export function presetOn(follow: ArrangeFollow, name: ArrangePreset): ArrangeFollow {
  const preset: ArrangeFollow = ARRANGE_PRESETS[name];
  const kept = follow.hints.filter((hint) => ARRANGE_HELD.includes(hint));
  return {
    ...preset,
    hints: ARRANGE_HINTS.filter((hint) => preset.hints.includes(hint) || kept.includes(hint)),
  };
}

/**
 * The preset `follow` is, or `null` where a switch has been moved off every one. The layers
 * and the amount are compared only where the preset's add-on reads them, and the held add-ons
 * not at all.
 */
export function followPreset(follow: ArrangeFollow): ArrangePreset | null {
  const fitted = fittedOf(follow.hints).join();
  const same = (one: ArrangeFollow): boolean =>
    one.strength === follow.strength &&
    one.hints.join() === fitted &&
    (!one.hints.includes("more") || one.more === follow.more) &&
    (!one.hints.includes("layers") ||
      ARRANGE_LAYERS.every(
        (layer) => one.layers.includes(layer) === follow.layers.includes(layer),
      ));
  return ARRANGE_PRESET_NAMES.find((name) => same(ARRANGE_PRESETS[name])) ?? null;
}
